import { isValid, parse, format, isAfter } from 'date-fns';
import { z } from 'zod';
import { getErrorMessage } from './getErrorMessage';
import { type AdminDataProvider } from '../../api/adminDataProvider';

export interface User {
  id: number;
  pictureUrl: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  phoneNumber: string | null;
  email: string | null;
  jobTitleId: number;
  departmentId: number;
  regionId: number | null;
  units: string[];
  disabledFrom: string | null;
}

export interface CurrentUser extends User {
  permissions: string[];
}

export type UserCsvValidationError = {
  property: string;
  message: string;
};

export interface UserLite {
  id: number;
  firstName?: string;
  lastName?: string;
}

const parseDate = (value: string, formats: string[]): string | null => {
  for (const f of formats) {
    const date = parse(value, f, new Date());
    if (isValid(date) && isAfter(date, new Date('1970-01-01'))) {
      return format(date, "yyyy-MM-dd'T'00:00:00.000'Z'");
    }
  }
  return null;
};

export const DATE_FORMATS = ['dd/MM/yyyy', 'dd-MM-yyyy', 'dd-MMM-yyyy'];

const dateSchema = z.string().transform((dateStr, ctx) => {
  const date = parseDate(dateStr, DATE_FORMATS);
  if (!date) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Invalid date format. Please use one of the following formats: dd/MM/yyyy, dd-MM-yyyy or dd-MMM-yyyy, and ensure it is after 1970',
    });
  }
  return date;
});

export const bulkAddUserSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  email: z
    .string()
    .trim()
    .transform((str) => (str ? str : undefined))
    .optional(),
  phoneNumber: z
    .string()
    .regex(
      /^\+\d{1,15}$/,
      'Phone number must be in format: +[countryCode][number]'
    ),
  internalId: z.string().trim().optional(),
  startDate: dateSchema,
  department: z.string().min(1, { message: 'Department is required' }),
  jobTitle: z.string().min(1, { message: 'Job title is required' }),
  region: z.string().min(1, { message: 'Region is required' }),
  unit: z.string().min(1, { message: 'Unit is required' }),
  languageCountryCode: z.enum(['en', 'ro'], {
    errorMap: () => ({ message: "Allowed languages: 'en' or 'ro'" }),
  }),
});

export const bulkUpdateUserSchema = z.object({
  id: z.string().optional(),
  internalId: z.string().trim().optional(),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  phoneNumber: z
    .string()
    .regex(
      /^\+\d{1,15}$/,
      'Phone number must be in format: +[countryCode][number]'
    ),
  pictureUrl: z.string().url().optional(),
  startDate: dateSchema,
  department: z.string().min(1, { message: 'Department is required' }),
  jobTitle: z.string().min(1, { message: 'Job title is required' }),
  region: z.string().min(1, { message: 'Region is required' }),
  unit: z.string().min(1, { message: 'Unit is required' }),
  languageCountryCode: z.enum(['en', 'ro'], {
    errorMap: () => ({ message: "Allowed languages: 'en' or 'ro'" }),
  }),
});

// Bulk disable
export interface DisableUserRow {
  id: string;
  internalId: string;
  firstName?: string;
  lastName?: string;
}

export interface DisableUserResultRow extends DisableUserRow {
  status: 'pending' | 'processing' | 'success' | 'error';
}

export type DisableUserExportRow = DisableUserRow & {
  errorMessage: string;
};

export const createDisableUserSchema = (dataProvider: AdminDataProvider) =>
  z
    .object({
      id: z.string().optional(),
      internalId: z.string().optional(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
    })
    .superRefine(async (data, ctx) => {
      if (!(data.id || data.internalId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Either 'id' or 'internalId' must be provided.",
          path: ['id'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Either 'id' or 'internalId' must be provided.",
          path: ['internalId'],
        });

        return;
      }

      if (data.id) {
        try {
          const res = await dataProvider.getOne('users', { id: data.id });
          if (!res.data) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "User with this id doesn't exist.",
              path: ['id'],
            });
          } else {
            data.internalId = res.data.internalId;
            data.firstName = res.data.firstName;
            data.lastName = res.data.lastName;
          }
        } catch {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "User with this id doesn't exist.",
            path: ['id'],
          });
        }
      }

      if (data.internalId) {
        const res = await dataProvider
          .getUserByInternalId(data.internalId)
          .catch((error: unknown) => ({ error }));
        if (res && 'error' in res) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: getErrorMessage(res.error),
            path: ['internalId'],
          });
          return;
        }
        if (!res) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "User with this internal id doesn't exist.",
            path: ['internalId'],
          });
        } else {
          data.id = res.id.toString();
          data.firstName = res.firstName;
          data.lastName = res.lastName;
        }
      }
    });

export type GetUserResponse = {
  id: number;
  internalId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber: string;
  startDate: string;
  department?: string;
  jobTitle?: string;
  region?: string;
  unit: string;
  languageCountryCode: 'en' | 'ro';
};

/**
 * Admin user endpoints currently return optional `firstName`/`lastName` but not `displayName`.
 * Keep this fallback aligned with `grow-api` `mapUserDisplayName` until admin responses
 * expose the guaranteed `displayName` field.
 *
 * TODO: Use the API-provided displayName once admin user responses include it.
 */
export const getAdminUserDisplayName = (user: {
  id: number | string;
  firstName?: string | null;
  lastName?: string | null;
}) => {
  const name = [user.firstName, user.lastName]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(' ');

  return name || `User ${user.id}`;
};
